/**
 * FHIR Observation Service
 * 
 * Provides CRUD operations for FHIR Observation resources and 
 * demo data seeding functionality for the MiEROS demo portal.
 * 
 * LOINC codes used for demo observations:
 * - 8867-4: Heart Rate (bpm)
 * - 8310-5: Body Temperature (°F)
 * - 59408-5: Oxygen Saturation (%)
 * - 2339-0: Blood Glucose (mg/dL)
 * - 8302-2: Body Height (cm)
 * - 29463-7: Body Weight (kg)
 * - 85354-9: Blood Pressure Panel
 * - 8480-6: Systolic Blood Pressure (mmHg)
 * - 8462-4: Diastolic Blood Pressure (mmHg)
 */

import axios from 'axios';
import config from '../utils/EnvironmentSwitcher';
import Logger from '../utils/Logger';
import { buildFhirHeaders } from '../utils/fhirAuth';

// FHIR Observation endpoint
const FHIR_ENDPOINT = `${String(config.apiUrl || '').replace(/\/+$/, '')}/api/fhir`;
// fhirSystemBase is canonical — FHIR identifier system URIs are permanent labels baked into
// stored resources and do NOT change between dev/prod environments.
const FHIR_ID_SYSTEM = `${String(config.fhirSystemBase || '').replace(/\/+$/, '')}/fhir/ids`;
const OBSERVATION_ENDPOINT = `${FHIR_ENDPOINT}/Observation`;
const PATIENT_ENDPOINT = `${FHIR_ENDPOINT}/Patient`;

// LOINC codes for demo observations
export const LOINC_CODES = {
  HEART_RATE: {
    code: '8867-4',
    display: 'Heart Rate',
    unit: 'bpm',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  BODY_TEMPERATURE: {
    code: '8310-5',
    display: 'Body Temperature',
    unit: '°F',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  OXYGEN_SATURATION: {
    code: '59408-5',
    display: 'Oxygen Saturation',
    unit: '%',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  BLOOD_GLUCOSE: {
    code: '2339-0',
    display: 'Blood Glucose',
    unit: 'mg/dL',
    system: 'http://unitsofmeasure.org',
    category: 'laboratory'
  },
  BODY_HEIGHT: {
    code: '8302-2',
    display: 'Body Height',
    unit: 'cm',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  BODY_WEIGHT: {
    code: '29463-7',
    display: 'Body Weight',
    unit: 'kg',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  SYSTOLIC_BP: {
    code: '8480-6',
    display: 'Systolic Blood Pressure',
    unit: 'mmHg',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  },
  DIASTOLIC_BP: {
    code: '8462-4',
    display: 'Diastolic Blood Pressure',
    unit: 'mmHg',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  }
};

// Demo observation value ranges (for generating realistic data)
const DEMO_VALUE_RANGES = {
  '8867-4': { min: 60, max: 100, normal: { min: 60, max: 100 } }, // Heart Rate
  '8310-5': { min: 97.0, max: 99.5, normal: { min: 97.8, max: 99.1 } }, // Body Temperature
  '59408-5': { min: 95, max: 100, normal: { min: 95, max: 100 } }, // Oxygen Saturation
  '2339-0': { min: 70, max: 140, normal: { min: 70, max: 100 } }, // Blood Glucose
  '8302-2': { min: 150, max: 190, normal: { min: 160, max: 180 } }, // Body Height
  '29463-7': { min: 50, max: 100, normal: { min: 60, max: 80 } }, // Body Weight
  '8480-6': { min: 100, max: 140, normal: { min: 100, max: 120 } }, // Systolic BP
  '8462-4': { min: 60, max: 90, normal: { min: 60, max: 80 } } // Diastolic BP
};

/**
 * Get authorization headers for FHIR API requests
 * @param {Object} authUser - Firebase auth user object
 * @returns {Object} Headers object for axios requests
 */
const getFhirHeaders = async (authUser) => {
  const headers = await buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, false);
  if (headers.Authorization) return headers;

  const refreshedHeaders = await buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, true);
  if (refreshedHeaders.Authorization) return refreshedHeaders;

  throw new Error('Missing Firebase ID token for /api/fhir proxy');
};

/**
 * Generate a random value within a specified range
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {number} Random value within range
 */
const generateRandomValue = (min, max, decimals = 1) => {
  const value = Math.random() * (max - min) + min;
  return parseFloat(value.toFixed(decimals));
};

/**
 * Generate a random date within the past N days
 * @param {number} daysBack - Number of days to go back
 * @returns {Date} Random date
 */
const generateRandomDate = (daysBack = 30) => {
  const now = new Date();
  const pastDate = new Date(now.getTime() - Math.random() * daysBack * 24 * 60 * 60 * 1000);
  return pastDate;
};

/**
 * Build a FHIR Observation resource
 * @param {Object} params - Observation parameters
 * @returns {Object} FHIR Observation resource
 */
export const buildObservationResource = ({
  loincCode,
  value,
  effectiveDateTime,
  patientReference,
  patientIdentifier,
  userId,
  status = 'final'
}) => {
  const loincInfo = Object.values(LOINC_CODES).find(l => l.code === loincCode) || {
    code: loincCode,
    display: 'Unknown',
    unit: '',
    system: 'http://unitsofmeasure.org',
    category: 'vital-signs'
  };

  return {
    resourceType: 'Observation',
    status,
    identifier: [
      {
        system: FHIR_ID_SYSTEM,
        value: userId
      }
    ],
    category: [
      {
        coding: [
          {
            system: 'http://terminology.hl7.org/CodeSystem/observation-category',
            code: loincInfo.category,
            display: loincInfo.category === 'vital-signs' ? 'Vital Signs' : 'Laboratory'
          }
        ]
      }
    ],
    code: {
      coding: [
        {
          system: 'http://loinc.org',
          code: loincInfo.code,
          display: loincInfo.display
        }
      ],
      text: loincInfo.display
    },
    subject: patientReference
      ? { reference: patientReference }
      : {
          identifier:
            patientIdentifier ||
            {
              system: FHIR_ID_SYSTEM,
              value: userId,
            },
        },
    effectiveDateTime: effectiveDateTime instanceof Date 
      ? effectiveDateTime.toISOString() 
      : effectiveDateTime,
    valueQuantity: {
      value: value,
      unit: loincInfo.unit,
      system: loincInfo.system,
      code: loincInfo.unit
    }
  };
};

/**
 * FHIR Observation Service Class
 */
class FhirObservationService {
  constructor() {
    this.endpoint = OBSERVATION_ENDPOINT;
    this.patientEndpoint = PATIENT_ENDPOINT;
  }

  /**
   * List all observations, optionally filtered
   * @param {Object} authUser - Firebase auth user
   * @param {Object} filters - Optional filters (patient, code, date range)
   * @returns {Promise<Array>} Array of Observation resources
   */
  async listObservations(authUser, filters = {}) {
    try {
      const headers = await getFhirHeaders(authUser);
      const params = new URLSearchParams();

      // Add filters
      if (filters.patient) {
        params.append('patient', filters.patient);
      }
      if (filters.code) {
        params.append('code', filters.code);
      }
      if (filters.dateStart) {
        params.append('date', `ge${filters.dateStart}`);
      }
      if (filters.dateEnd) {
        params.append('date', `le${filters.dateEnd}`);
      }
      if (authUser?.uid) {
        params.append('identifier', `${FHIR_ID_SYSTEM}|${authUser.uid}`);
      }
      // Reduce server-side pagination churn by requesting larger pages.
      params.append('_count', '100');

      const queryString = params.toString();
      const url = queryString ? `${this.endpoint}?${queryString}` : this.endpoint;

      Logger.debug('Fetching observations from:', url);

      let allObservations = [];
      let nextUrl = url;
      let pageCount = 0;
      const visitedUrls = new Set();
      const MAX_PAGES = 25;

      // Handle pagination
      while (nextUrl) {
        if (visitedUrls.has(nextUrl) || pageCount >= MAX_PAGES) {
          break;
        }
        visitedUrls.add(nextUrl);
        pageCount += 1;

        const response = await axios.get(nextUrl, { headers });
        
        if (response.data.entry) {
          allObservations = [...allObservations, ...response.data.entry.map(e => e.resource)];
        }

        // Check for next page
        nextUrl = null;
        if (response.data.link) {
          const nextLink = response.data.link.find(link => link.relation === 'next');
          if (nextLink) {
            nextUrl = nextLink.url.replace('http://', 'https://');
          }
        }
      }

      Logger.debug(`Total observations fetched: ${allObservations.length}`);
      return allObservations;
    } catch (error) {
      Logger.error('Error listing observations:', error);
      throw error;
    }
  }

  /**
   * Get a single observation by ID
   * @param {string} id - Observation ID
   * @param {Object} authUser - Firebase auth user
   * @returns {Promise<Object>} Observation resource
   */
  async getObservation(id, authUser) {
    try {
      const headers = await getFhirHeaders(authUser);
      const response = await axios.get(`${this.endpoint}/${id}`, { headers });
      return response.data;
    } catch (error) {
      Logger.error(`Error getting observation ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new observation
   * @param {Object} observation - FHIR Observation resource
   * @param {Object} authUser - Firebase auth user
   * @returns {Promise<Object>} Created Observation resource
   */
  async createObservation(observation, authUser) {
    try {
      const headers = await getFhirHeaders(authUser);
      const response = await axios.post(this.endpoint, observation, { headers });
      Logger.debug('Created observation:', response.data.id);
      return response.data;
    } catch (error) {
      Logger.error('Error creating observation:', error);
      throw error;
    }
  }

  /**
   * Update an existing observation
   * @param {string} id - Observation ID
   * @param {Object} observation - Updated FHIR Observation resource
   * @param {Object} authUser - Firebase auth user
   * @returns {Promise<Object>} Updated Observation resource
   */
  async updateObservation(id, observation, authUser) {
    try {
      const headers = await getFhirHeaders(authUser);
      const observationWithId = { ...observation, id };
      const response = await axios.put(`${this.endpoint}/${id}`, observationWithId, { headers });
      Logger.debug('Updated observation:', id);
      return response.data;
    } catch (error) {
      Logger.error(`Error updating observation ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete an observation
   * @param {string} id - Observation ID
   * @param {Object} authUser - Firebase auth user
   * @returns {Promise<void>}
   */
  async deleteObservation(id, authUser) {
    try {
      const headers = await getFhirHeaders(authUser);
      await axios.delete(`${this.endpoint}/${id}`, { headers });
      Logger.debug('Deleted observation:', id);
    } catch (error) {
      Logger.error(`Error deleting observation ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create or get a demo patient
   * @param {Object} authUser - Firebase auth user
   * @param {Object} patientData - Patient demographic data
   * @returns {Promise<string>} Patient reference (e.g., "Patient/123")
   */
  async createOrGetDemoPatient(authUser, patientData = {}) {
    try {
      const headers = await getFhirHeaders(authUser);

      // Check if patient already exists for this user
      const searchUrl = `${this.patientEndpoint}?identifier=${encodeURIComponent(`${FHIR_ID_SYSTEM}|${authUser.uid}`)}`;
      const searchResponse = await axios.get(searchUrl, { headers });

      if (searchResponse.data.entry && searchResponse.data.entry.length > 0) {
        const existingPatient = searchResponse.data.entry[0].resource;
        Logger.debug('Found existing patient:', existingPatient.id);
        return `Patient/${existingPatient.id}`;
      }

      // Create new patient
      const patient = {
        resourceType: 'Patient',
        identifier: [
          {
            system: FHIR_ID_SYSTEM,
            value: authUser.uid
          }
        ],
        name: [
          {
            use: 'official',
            family: patientData.lastName || authUser.displayName?.split(' ').pop() || 'Demo',
            given: [patientData.firstName || authUser.displayName?.split(' ')[0] || 'User']
          }
        ],
        gender: patientData.gender || 'unknown',
        birthDate: patientData.birthDate || '1990-01-01',
        telecom: authUser.email ? [
          {
            system: 'email',
            value: authUser.email
          }
        ] : []
      };

      const response = await axios.post(this.patientEndpoint, patient, { headers });
      Logger.debug('Created demo patient:', response.data.id);
      return `Patient/${response.data.id}`;
    } catch (error) {
      Logger.error('Error creating/getting demo patient:', error);
      throw error;
    }
  }

  /**
   * Seed demo observations for a user
   * This creates a realistic set of historical vital signs data
   * @param {Object} authUser - Firebase auth user
   * @param {Object} options - Seeding options
   * @returns {Promise<Array>} Array of created observation IDs
   */
  async seedDemoObservations(authUser, options = {}) {
    const {
      daysOfData = 30,
      observationsPerDay = 1,
      observationTypes = ['HEART_RATE', 'BODY_TEMPERATURE', 'OXYGEN_SATURATION', 'BLOOD_GLUCOSE']
    } = options;

    try {
      Logger.debug('Starting demo observation seeding for user:', authUser.uid);

      // Check if demo data already exists
      const existingObservations = await this.listObservations(authUser, {});
      if (existingObservations.length > 0) {
        Logger.debug('Demo data already exists, skipping seeding');
        return { skipped: true, existingCount: existingObservations.length };
      }

      // Bind observations directly to the logged-in user's FHIR identifier.
      // Do not create Patient resources from this flow.
      const patientReference = null;
      const patientIdentifier = {
        system: FHIR_ID_SYSTEM,
        value: authUser.uid,
      };

      const createdIds = [];
      const now = new Date();

      // Generate observations for each day
      for (let dayOffset = 0; dayOffset < daysOfData; dayOffset++) {
        const observationDate = new Date(now);
        observationDate.setDate(observationDate.getDate() - dayOffset);

        // Set a random time during the day for more realistic data
        observationDate.setHours(
          6 + Math.floor(Math.random() * 12), // Between 6 AM and 6 PM
          Math.floor(Math.random() * 60),
          Math.floor(Math.random() * 60)
        );

        // Create observations for each type
        for (const typeName of observationTypes) {
          const loincInfo = LOINC_CODES[typeName];
          if (!loincInfo) continue;

          const valueRange = DEMO_VALUE_RANGES[loincInfo.code];
          if (!valueRange) continue;

          // Generate multiple observations per day if requested
          for (let i = 0; i < observationsPerDay; i++) {
            const value = generateRandomValue(valueRange.min, valueRange.max);

            const observation = buildObservationResource({
              loincCode: loincInfo.code,
              value,
              effectiveDateTime: observationDate,
              patientReference,
              patientIdentifier,
              userId: authUser.uid
            });

            try {
              const created = await this.createObservation(observation, authUser);
              createdIds.push(created.id);
            } catch (err) {
              Logger.warn(`Failed to create observation for ${typeName}:`, err.message);
            }

            // Offset time for multiple observations per day
            if (i < observationsPerDay - 1) {
              observationDate.setHours(observationDate.getHours() + Math.floor(14 / observationsPerDay));
            }
          }
        }
      }

      Logger.debug(`Seeded ${createdIds.length} demo observations`);
      return { 
        success: true, 
        createdCount: createdIds.length, 
        ids: createdIds 
      };
    } catch (error) {
      Logger.error('Error seeding demo observations:', error);
      throw error;
    }
  }

  /**
   * Get observation statistics grouped by type
   * @param {Object} authUser - Firebase auth user
   * @param {Date} startDate - Start date for range
   * @param {Date} endDate - End date for range
   * @returns {Promise<Object>} Statistics by observation type
   */
  async getObservationStats(authUser, startDate, endDate) {
    try {
      const observations = await this.listObservations(authUser, {
        dateStart: startDate?.toISOString(),
        dateEnd: endDate?.toISOString()
      });

      const stats = {};

      observations.forEach(obs => {
        const code = obs.code?.coding?.[0]?.code;
        const display = obs.code?.coding?.[0]?.display || obs.code?.text || 'Unknown';
        const value = obs.valueQuantity?.value;

        if (!code || value === undefined) return;

        if (!stats[code]) {
          stats[code] = {
            code,
            display,
            unit: obs.valueQuantity?.unit || '',
            values: [],
            count: 0
          };
        }

        stats[code].values.push({
          value,
          date: obs.effectiveDateTime
        });
        stats[code].count++;
      });

      // Calculate min, max, avg for each type
      Object.keys(stats).forEach(code => {
        const values = stats[code].values.map(v => v.value);
        stats[code].min = Math.min(...values);
        stats[code].max = Math.max(...values);
        stats[code].avg = parseFloat((values.reduce((a, b) => a + b, 0) / values.length).toFixed(2));
        stats[code].latest = stats[code].values[0]?.value;
      });

      return stats;
    } catch (error) {
      Logger.error('Error getting observation stats:', error);
      throw error;
    }
  }

  /**
   * Clear all demo observations for a user
   * Use with caution - this deletes all observations!
   * @param {Object} authUser - Firebase auth user
   * @returns {Promise<number>} Count of deleted observations
   */
  async clearDemoObservations(authUser) {
    try {
      const observations = await this.listObservations(authUser);
      let deletedCount = 0;

      for (const obs of observations) {
        try {
          await this.deleteObservation(obs.id, authUser);
          deletedCount++;
        } catch (err) {
          Logger.warn(`Failed to delete observation ${obs.id}:`, err.message);
        }
      }

      Logger.debug(`Cleared ${deletedCount} demo observations`);
      return deletedCount;
    } catch (error) {
      Logger.error('Error clearing demo observations:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const fhirObservationService = new FhirObservationService();

// Export class for testing or custom instances
export default FhirObservationService;
